'use client'

import { useState, useEffect } from 'react'
import { useParams } from 'next/navigation'
import Image from 'next/image'
import { Save, Upload, X, Check, Loader2, AlertCircle } from 'lucide-react'

interface Memorial {
  id: string
  slug: string
  firstName: string | null
  lastName: string | null
  birthName: string | null
  dateOfBirth: string | null
  dateOfDeath: string | null
  cityOfDeath: string | null
  placeOfResidence: string | null
  city: string | null
  region: string | null
  postalCode: string | null
  descriptions: string | null
  imagePath: string | null
}

export default function EditMemorialPage() {
  const params = useParams()
  const slug = params.slug as string

  const [memorial, setMemorial] = useState<Memorial | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    birthName: '',
    dateOfBirth: '',
    dateOfDeath: '',
    cityOfDeath: '',
    placeOfResidence: '',
    city: '',
    descriptions: '',
  })

  // Fetch memorial data
  useEffect(() => {
    const fetchMemorial = async () => {
      try {
        // Use family API to work with unpublished memorials
        const res = await fetch(`/api/family/${slug}`)
        if (!res.ok) throw new Error('Memorial not found')
        const data = await res.json()
        setMemorial(data)
        setFormData({
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          birthName: data.birthName || '',
          dateOfBirth: data.dateOfBirth ? data.dateOfBirth.split('T')[0] : '',
          dateOfDeath: data.dateOfDeath ? data.dateOfDeath.split('T')[0] : '',
          cityOfDeath: data.cityOfDeath || '',
          placeOfResidence: data.placeOfResidence || '',
          city: data.city || '',
          descriptions: data.descriptions || '',
        })
      } catch (err) {
        console.error(err)
        setError('Gedenkseite konnte nicht geladen werden')
      } finally {
        setLoading(false)
      }
    }
    fetchMemorial()
  }, [slug])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }))
    setSaved(false)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!memorial) return

    setSaving(true)
    setError(null)

    try {
      const res = await fetch(`/api/admin/memorials/${memorial.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      })

      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || 'Fehler beim Speichern')
      }

      const updated = await res.json()
      setMemorial(updated)
      setSaved(true)
      setTimeout(() => setSaved(false), 5000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Speichern')
    } finally {
      setSaving(false)
    }
  }

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !memorial) return

    setUploading(true)
    setError(null)

    try {
      // Upload the file
      const uploadFormData = new FormData()
      uploadFormData.append('file', file)
      uploadFormData.append('type', 'portrait')
      uploadFormData.append('memorialId', memorial.id)

      const uploadRes = await fetch('/api/admin/upload', {
        method: 'POST',
        body: uploadFormData,
      })

      if (!uploadRes.ok) throw new Error('Upload fehlgeschlagen')

      const uploadData = await uploadRes.json()

      // Save the image path to the database
      const saveRes = await fetch(`/api/admin/memorials/${memorial.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imagePath: uploadData.url }),
      })

      if (!saveRes.ok) throw new Error('Speichern fehlgeschlagen')

      // Add cache-busting timestamp to force reload
      const imageUrl = `${uploadData.url}?t=${Date.now()}`
      setMemorial((prev) => (prev ? { ...prev, imagePath: imageUrl } : null))
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
    } catch (err) {
      setError('Bild-Upload fehlgeschlagen')
      console.error(err)
    } finally {
      setUploading(false)
    }
  }

  const handleRemoveImage = async () => {
    if (!memorial) return

    try {
      const res = await fetch(`/api/admin/memorials/${memorial.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imagePath: null }),
      })

      if (!res.ok) throw new Error('Fehler beim Entfernen')

      setMemorial((prev) => (prev ? { ...prev, imagePath: null } : null))
    } catch (err) {
      setError('Bild konnte nicht entfernt werden')
      console.error(err)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-memorial-primary" />
      </div>
    )
  }

  if (error && !memorial) {
    return (
      <div className="text-center py-16">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h1 className="text-2xl font-serif text-gray-900">{error}</h1>
      </div>
    )
  }

  return (
    <>
    {/* Toast notification */}
    {saved && (
      <div className="fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3 bg-green-600 text-white rounded-lg shadow-lg animate-fade-in">
        <Check className="w-5 h-5" />
        <span className="font-medium">Änderungen gespeichert!</span>
      </div>
    )}
    <div className="max-w-4xl space-y-8">
      {/* Page header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-serif text-gray-900">Gedenkseite bearbeiten</h1>
        <p className="text-gray-500 mt-1">Aktualisieren Sie die Informationen der Gedenkseite</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Portrait image */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Portraitbild</h2>
          <div className="flex items-start gap-6">
            <div className="relative w-32 h-40 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
              {memorial?.imagePath ? (
                <Image src={memorial.imagePath} alt="Portrait" fill className="object-cover" />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center text-gray-400">
                  <span className="text-3xl font-serif">
                    {formData.firstName?.charAt(0)}
                    {formData.lastName?.charAt(0)}
                  </span>
                </div>
              )}
              {uploading && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <Loader2 className="w-6 h-6 animate-spin text-white" />
                </div>
              )}
            </div>
            <div className="flex-1">
              <p className="text-sm text-gray-600 mb-4">
                Laden Sie ein würdevolles Portraitbild hoch. Empfohlene Größe: 400x500 Pixel.
              </p>
              <div className="flex gap-3">
                <label className="flex items-center gap-2 px-4 py-2 bg-memorial-primary text-white rounded-lg hover:bg-memorial-primary/90 transition-colors cursor-pointer">
                  <Upload className="w-4 h-4" />
                  Bild hochladen
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    disabled={uploading}
                  />
                </label>
                {memorial?.imagePath && (
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <X className="w-4 h-4" />
                    Entfernen
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Personal information */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Persönliche Daten</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Vorname</label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-memorial-primary/20 focus:border-memorial-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nachname</label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-memorial-primary/20 focus:border-memorial-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Geburtsname (optional)
              </label>
              <input
                type="text"
                name="birthName"
                value={formData.birthName}
                onChange={handleChange}
                placeholder="Falls abweichend"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-memorial-primary/20 focus:border-memorial-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sterbeort</label>
              <input
                type="text"
                name="cityOfDeath"
                value={formData.cityOfDeath}
                onChange={handleChange}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-memorial-primary/20 focus:border-memorial-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Geburtsdatum</label>
              <input
                type="date"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleChange}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-memorial-primary/20 focus:border-memorial-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sterbedatum</label>
              <input
                type="date"
                name="dateOfDeath"
                value={formData.dateOfDeath}
                onChange={handleChange}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-memorial-primary/20 focus:border-memorial-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Wohnort</label>
              <input
                type="text"
                name="placeOfResidence"
                value={formData.placeOfResidence}
                onChange={handleChange}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-memorial-primary/20 focus:border-memorial-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Stadt</label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-memorial-primary/20 focus:border-memorial-primary"
              />
            </div>
          </div>
        </div>

        {/* Biography */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Nachruf / Biografie</h2>
          <p className="text-sm text-gray-600 mb-4">
            Schreiben Sie einen würdevollen Nachruf. Absätze werden durch Leerzeilen getrennt.
          </p>
          <textarea
            name="descriptions"
            value={formData.descriptions}
            onChange={handleChange}
            rows={10}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-memorial-primary/20 focus:border-memorial-primary resize-y"
            placeholder="Schreiben Sie hier den Nachruf..."
          />
          <p className="text-xs text-gray-500 mt-2">{formData.descriptions.length} Zeichen</p>
        </div>

        {/* Error message */}
        {error && (
          <div className="flex items-center gap-2 p-4 bg-red-50 text-red-700 rounded-lg">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            {error}
          </div>
        )}

        {/* Submit button */}
        <div className="flex justify-end gap-4">
          <button
            type="button"
            className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex items-center gap-2 px-6 py-2.5 bg-memorial-primary text-white rounded-lg hover:bg-memorial-primary/90 transition-colors disabled:opacity-50"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Speichert...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Änderungen speichern
              </>
            )}
          </button>
        </div>
      </form>
    </div>
    </>
  )
}
